import { PageControls, usePagination } from '@/components/shared/pagination/Pagination';
import tableStrings from '@/components/shared/table/table.translations';
import { TableSelectFilter, TableSelectFilterOption } from '@/components/shared/table/TableSelectFilter';
import { RequestStatus, RequestType } from '@/pages/team/teamMember/requests/RequestsTable.types';
import { StatusChip, statusLabels } from '@/pages/team/teamMember/requests/RequestStatusChip';
import { strings } from '@/services/translation/strings';
import { FC, useMemo, useState } from 'react';
import { FiClock } from 'react-icons/fi';
import { MyRequestRow } from './useMyRequestRows';

const pageSize = 20;

type StatusFilterValue = RequestStatus | 'All';
type TypeFilterValue = RequestType | 'All';

const STATUS_OPTIONS: TableSelectFilterOption<StatusFilterValue>[] = [
  { label: strings.timeshift.teamMember.requests.filters.status, value: 'All' },
  { label: statusLabels[RequestStatus.Approved], value: RequestStatus.Approved },
  { label: statusLabels[RequestStatus.Pending], value: RequestStatus.Pending },
  { label: statusLabels[RequestStatus.CancelledByEmployee], value: RequestStatus.CancelledByEmployee },
  { label: statusLabels[RequestStatus.CancelledByManager], value: RequestStatus.CancelledByManager },
  { label: statusLabels[RequestStatus.Denied], value: RequestStatus.Denied }
];

const TYPE_OPTIONS: TableSelectFilterOption<TypeFilterValue>[] = [
  { label: strings.timeshift.teamMember.requests.filters.requestType, value: 'All' },
  { label: RequestType.LeaveRequest, value: RequestType.LeaveRequest },
  { label: RequestType.ScheduleChange, value: RequestType.ScheduleChange },
  { label: RequestType.AvailabilityChange, value: RequestType.AvailabilityChange }
];

const formatShort = (date: string) => new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

function formatDates(request: MyRequestRow): string {
  if (!request.startDate) return '';
  if (!request.endDate || request.endDate === request.startDate) return formatShort(request.startDate);
  return `${formatShort(request.startDate)} - ${formatShort(request.endDate)}`;
}

interface RequestCardProps {
  request: MyRequestRow;
  onOpen: (request: MyRequestRow) => void;
}

const RequestCard: FC<RequestCardProps> = ({ request, onOpen }) => {
  const dates = formatDates(request);
  const showReason = request.status === RequestStatus.Denied && !!request.denialReason;

  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(request)}
        className="flex w-full items-start justify-between gap-4 rounded-xl bg-primary-std px-4 py-4 text-left shadow-sm hover:bg-secondary-std focus-visible:outline focus-visible:outline-2 items-center"
      >
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-primary-std">{request.title}</p>
          {dates && (
            <p className="mt-1 flex items-center gap-2 text-sm text-secondary-std">
              <FiClock aria-hidden="true" />
              {dates}
            </p>
          )}
          {showReason && (
            <p className="mt-1 truncate text-sm text-secondary-std" title={request.denialReason}>
              {request.denialReason}
            </p>
          )}
        </div>
        <StatusChip status={request.status} />
      </button>
    </li>
  );
};

interface MyRequestsListProps {
  requests: MyRequestRow[];
  onOpen: (request: MyRequestRow) => void;
}

export const MyRequestsList: FC<MyRequestsListProps> = ({ requests, onOpen }) => {
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('All');
  const [typeFilter, setTypeFilter] = useState<TypeFilterValue>('All');

  // Newest first by submission date
  const visible = useMemo(
    () =>
      requests
        .filter(r => statusFilter === 'All' || r.status === statusFilter)
        .filter(r => typeFilter === 'All' || r.type === typeFilter)
        .sort((a, b) => new Date(b.dateSubmitted).getTime() - new Date(a.dateSubmitted).getTime()),
    [requests, statusFilter, typeFilter]
  );

  const [offset, limit, controls] = usePagination(pageSize, visible.length);
  const page = visible.slice(offset, offset + limit);

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="flex items-center justify-start space-x-3 p-4">
        <p className="text-sm font-semibold text-primary-std">
          {strings.timeshift.teamMember.requests.filters.filter}:
        </p>
        <TableSelectFilter
          label={strings.timeshift.teamMember.requests.filters.status}
          value={statusFilter}
          options={STATUS_OPTIONS}
          onChange={setStatusFilter}
        />
        <TableSelectFilter
          label={strings.timeshift.teamMember.requests.filters.requestType}
          value={typeFilter}
          options={TYPE_OPTIONS}
          onChange={setTypeFilter}
        />
      </div>

      {visible.length > 0 ? (
        <>
          <ul className="flex flex-col gap-3">
            {page.map(request => (
              <RequestCard key={`${request.type}-${request.id}`} request={request} onOpen={onOpen} />
            ))}
          </ul>
          <PageControls {...controls} />
        </>
      ) : (
        <p className="py-5 text-center text-xs font-medium uppercase text-primary-std">{tableStrings.nothingToShow}</p>
      )}
    </div>
  );
};
